import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

const desktop = '(min-width: 990px)';
const emit = (node, name, detail = {}) => node.dispatchEvent(new CustomEvent('tahho:analytics', {
  bubbles: true, detail: { name, ...detail },
}));

class TahhoGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.tiles = [...this.querySelectorAll('.tahho-gallery-tile')];
    this.slides = [...this.querySelectorAll('[data-full-slide]')];
    this.dialog = this.querySelector('dialog');
    this.index = 0;
    this.classList.add('is-ready');
    this.addEventListener('click', (event) => {
      const control = event.target.closest('button, [data-gallery-open]');
      if (!control) return;
      if (control.hasAttribute('data-gallery-open')) {
        event.preventDefault();
        this.opener = control;
        this.showFull(Number(control.dataset.galleryOpen));
        this.dialog.showModal();
        this.playMedia(this.slides[this.fullIndex]);
        this.dialog.focus({ preventScroll: true });
        emit(this, 'gallery_fullscreen_open');
      } else if (control.hasAttribute('data-gallery-step')) {
        this.select(this.index + Number(control.dataset.galleryStep));
      } else if (control.hasAttribute('data-gallery-select')) {
        this.select(Number(control.dataset.gallerySelect));
      } else if (control.hasAttribute('data-full-step')) {
        this.showFull(this.fullIndex + Number(control.dataset.fullStep));
      } else if (control.hasAttribute('data-dialog-close')) {
        this.dialog.close();
      } else if (control.hasAttribute('data-zoom')) {
        const zoomed = this.dialog.classList.toggle('is-zoomed');
        control.setAttribute('aria-pressed', String(zoomed));
      }
    }, { signal });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (matchMedia(desktop).matches) return;
        const rect = this.track.getBoundingClientRect();
        const distances = this.tiles.map(tile => Math.abs(tile.getBoundingClientRect().left - rect.left));
        this.update(distances.indexOf(Math.min(...distances)));
      });
    }, { passive: true, signal });
    this.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (event.target.closest('video, iframe, model-viewer')) return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      if (this.dialog.open) this.showFull(this.fullIndex + step);
      else this.select(this.index + step);
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.pauseMedia();
      this.opener?.focus({ preventScroll: true });
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); cancelAnimationFrame(this.frame); }
  pauseMedia() {
    this.querySelectorAll('video').forEach(video => video.pause());
    this.querySelectorAll('iframe').forEach(frame => {
      // Reload only an already loaded external player, stopping playback on close/change.
      if (frame.src) frame.src = frame.src;
    });
  }
  playMedia(container) {
    if (!container) return;
    container.querySelectorAll('video').forEach(video => {
      video.muted = true;
      video.play().catch(() => {});
    });
    container.querySelectorAll('iframe').forEach(frame => {
      frame.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      frame.contentWindow?.postMessage('{"method":"play"}', '*');
    });
  }
  select(index) {
    index = (index + this.tiles.length) % this.tiles.length;
    this.track.scrollTo({ left: this.tiles[index].offsetLeft - this.track.firstElementChild.offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    this.update(index);
  }
  selectMedia(mediaId, animate = true) {
    const index = this.tiles.findIndex(tile => tile.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    if (matchMedia(desktop).matches) {
      // Promote even images outside the five visible desktop collage tiles.
      this.track.prepend(this.tiles[index]);
    } else {
      const first = this.track.firstElementChild;
      this.track.scrollTo({ left: this.tiles[index].offsetLeft - first.offsetLeft,
        behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
    }
    this.update(index);
  }
  openMedia(mediaId, opener) {
    const index = this.tiles.findIndex(tile => tile.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    this.opener = opener;
    this.showFull(index);
    this.dialog.showModal();
    this.playMedia(this.slides[index]);
    this.dialog.focus({ preventScroll: true });
    emit(this, 'gallery_fullscreen_open');
  }
  update(index) {
    if (index !== this.index) emit(this, 'gallery_image_view', { index });
    this.index = index;
    this.tiles.forEach((tile, tileIndex) => {
      const video = tile.querySelector('video');
      if (!video) return;
      if (tileIndex === index && !matchMedia(desktop).matches) {
        tile.classList.add('is-playing');
        video.muted = true;
        video.play().catch(() => {});
      } else {
        tile.classList.remove('is-playing');
        video.pause();
      }
    });
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = `${index + 1} / ${this.tiles.length}`;
    this.querySelectorAll('[data-gallery-select]').forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
  }
  showFull(index) {
    this.pauseMedia();
    this.fullIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => { slide.hidden = i !== this.fullIndex; });
    this.dialog.classList.remove('is-zoomed');
    const zoom = this.querySelector('[data-zoom]');
    zoom.setAttribute('aria-pressed', 'false');
    zoom.hidden = !this.slides[this.fullIndex].querySelector('img');
    this.querySelector('[data-dialog-counter]').textContent = `${this.fullIndex + 1} / ${this.slides.length}`;
    if (this.dialog.open) this.playMedia(this.slides[this.fullIndex]);
  }
}

class TahhoProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.classList.add('is-ready');
    this.sticky = this.querySelector('[data-sticky]');
    this.nativeButton = () => this.querySelector('.tahho-purchase button[name="add"]');
    this.section = this.closest('.shopify-section') || this;
    this.pending = false;
    this.generation = 0;
    // Child custom elements may connect after their parent during HTML parsing.
    customElements.whenDefined('tahho-gallery').then(() => {
      if (this.isConnected) this.syncVariantPreview();
    });
    this.section.addEventListener(StandardEvents.productSelect, event => {
      if (event.target.closest('product-card')) return;
      this.pending = true;
      this.previewOptionId = event.detail?.optionValueId;
      this.querySelector('[data-variant-preview]')?.setAttribute('aria-busy', 'true');
      const generation = ++this.generation;
      this.syncBuy();
      event.promise.then(({ detail }) => {
        if (generation !== this.generation) return;
        const field = this.querySelector('[name="selected_variant"]');
        if (field) field.value = detail?.resource?.id ?? '';
        this.syncVariantPreview(detail?.html, Boolean(detail?.resource));
        const nextSticky = detail?.html?.querySelector('[data-sticky]');
        const currentPrice = this.querySelector('[data-sticky-price]');
        const nextPrice = nextSticky?.querySelector('[data-sticky-price]');
        if (currentPrice && nextPrice) {
          currentPrice.replaceChildren(...[...nextPrice.childNodes].map(node => node.cloneNode(true)));
          for (const name of ['unitPriceCents', 'currency', 'unitPriceFormatted', 'unitLabel']) {
            currentPrice.dataset[name] = nextPrice.dataset[name] || '';
          }
          this.syncStickyPrice(this.quantity());
        }
        emit(this, 'variant_select', { variant_id: detail?.resource?.id ?? null });
      }).catch(() => {}).finally(() => {
        if (generation !== this.generation) return;
        this.pending = false;
        this.querySelector('[data-variant-preview]')?.removeAttribute('aria-busy');
        this.syncBuy();
      });
    }, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, event => {
      if (event.detail.cartLine) return;
      this.syncStickyPrice(event.detail.quantity);
    }, { signal });
    this.addEventListener('click', event => {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.hasAttribute('data-variant-preview-open')) {
        const preview = target.closest('[data-variant-preview]');
        this.querySelector('tahho-gallery')?.openMedia(preview?.dataset.mediaId, target);
      }
      if (target.hasAttribute('data-variant-preview-close')) {
        // Cancel an image decode still in flight, so dismissal always wins.
        this.previewGeneration = (this.previewGeneration || 0) + 1;
        this.setPreviewVisible(false, true);
      }
      if (target.hasAttribute('data-sticky-buy')) {
        const button = this.nativeButton();
        if (button && !button.disabled && !this.pending) button.click();
      }
      if (target.dataset.event) emit(this, target.dataset.event);
      if (target.hasAttribute('data-review-expand')) {
        const expanded = target.getAttribute('aria-expanded') !== 'true';
        target.setAttribute('aria-expanded', String(expanded));
        target.previousElementSibling.classList.toggle('is-expanded', expanded);
        target.textContent = expanded ? target.dataset.less : target.dataset.more;
      }
      if (target.hasAttribute('data-description-open')) this.openDescription(target);
      if (target.hasAttribute('data-description-close')) target.closest('dialog')?.close();
      if (target.hasAttribute('data-review-previous') || target.hasAttribute('data-review-next')) {
        const carousel = target.closest('[data-review-carousel]');
        this.stepReviews(carousel, target.hasAttribute('data-review-next') ? 1 : -1);
      }
      if (target.closest('.tahho-action') && matchMedia(desktop).matches && (target.name === 'add' || target.dataset.lead)) emit(this, 'desktop_action_panel_cta');
    }, { signal });
    this.querySelectorAll('details[data-event]').forEach(details => details.addEventListener('toggle', () => {
      if (details.open) emit(this, details.dataset.event);
    }, { signal }));
    this.querySelectorAll('[data-scroll-event]').forEach(rail => rail.addEventListener('scroll', () => emit(this, rail.dataset.scrollEvent), { once: true, passive: true, signal }));
    this.querySelectorAll('[data-review-expand]').forEach(button => {
      const copy = button.previousElementSibling;
      button.hidden = copy.scrollHeight <= copy.clientHeight + 1;
    });
    this.setupDescriptions(signal);
    this.setupReviews(signal);
    this.setupCraftMedia(signal);
    const units = [...this.querySelectorAll('[data-dimension$="-unit"]')];
    try { const saved = sessionStorage.getItem('tahho-unit'); if (['in','ft','cm','m'].includes(saved)) units.forEach(unit => { unit.value = saved; }); } catch {}
    this.addEventListener('input', () => this.validateDimensions(), { signal });
    this.addEventListener('change', event => {
      this.validateDimensions();
      try { if (units.includes(event.target)) sessionStorage.setItem('tahho-unit', event.target.value); } catch {}
    }, { signal });
    this.formObserver = new MutationObserver(() => this.syncBuy());
    const purchase = this.querySelector('.tahho-purchase');
    if (purchase) this.formObserver.observe(purchase, { subtree: true, childList: true, attributes: true, characterData: true });
    this.modalObserver = new MutationObserver(() => this.syncVisibility());
    this.modalObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    document.addEventListener('focusin', () => this.syncVisibility(), { signal });
    document.addEventListener('focusout', () => setTimeout(() => this.syncVisibility(), 0), { signal });
    window.visualViewport?.addEventListener('resize', () => this.syncVisibility(), { signal });
    window.addEventListener('resize', () => this.syncVisibility(), { signal });
    this.syncBuy(); this.syncVisibility(); this.validateDimensions();
    emit(this, 'product_view', { product_id: this.dataset.productId, product_mode: this.dataset.mode });
  }
  disconnectedCallback() { this.controller?.abort(); this.formObserver?.disconnect(); this.modalObserver?.disconnect(); this.resizeObservers?.forEach(observer => observer.disconnect()); }
  syncVariantPreview(html, validVariant = true) {
    const current = this.querySelector('[data-variant-preview]');
    const next = html?.querySelector('[data-variant-preview]');
    if (!current) return;
    const previousId = this.previewMediaId || current.dataset.mediaId;
    const nextId = next?.dataset.mediaId || current.dataset.mediaId;
    const changedImage = previousId !== nextId;
    const generation = this.previewGeneration = (this.previewGeneration || 0) + 1;
    if (html && (!validVariant || !next || next.dataset.matchedMedia !== 'true')) {
      this.previewMediaId = nextId;
      this.setPreviewVisible(false);
      return;
    }
    if (next) {
      const image = current.querySelector('img');
      const nextImage = next.querySelector('img');
      // Keep the focused button and sticky box stable; only update their contents.
      if (image && nextImage && current.dataset.mediaId !== next.dataset.mediaId) {
        const replacement = nextImage.cloneNode(true);
        replacement.loading = 'eager';
        image.replaceWith(replacement);
      } else if (image && nextImage) {
        image.alt = nextImage.alt;
      }
      current.dataset.mediaId = next.dataset.mediaId;
      current.dataset.matchedMedia = next.dataset.matchedMedia;
      current.querySelector('button').setAttribute('aria-label', next.querySelector('button').getAttribute('aria-label'));
    }
    const gallery = this.querySelector('tahho-gallery');
    // Do not reset a user's gallery browsing when only size/quantity changes.
    if (gallery && current.dataset.mediaId !== this.previewMediaId) {
      this.previewMediaId = current.dataset.mediaId;
      gallery.selectMedia(current.dataset.mediaId, false);
    }
    // Initial loads, including variant deep links, never reveal the purchase preview.
    // A dismissed preview stays dismissed across size changes that reuse its photo.
    if (!html || !changedImage) return;
    const image = current.querySelector('img');
    const ready = image?.decode ? image.decode() : Promise.resolve();
    ready.then(() => {
      if (!this.isConnected || generation !== this.previewGeneration) return;
      this.setPreviewVisible(true);
    }).catch(() => {
      if (generation === this.previewGeneration) this.setPreviewVisible(false);
    });
  }
  setPreviewVisible(visible, returnFocus = false) {
    const preview = this.querySelector('[data-variant-preview]');
    if (!preview) return;
    const selected = [...this.querySelectorAll('variant-picker input:checked')];
    const input = selected.find(node => node.dataset.optionValueId === this.previewOptionId) || selected.at(-1);
    const anchor = input?.closest('label');
    const before = anchor?.getBoundingClientRect();
    if (visible) {
      const image = preview.querySelector('img');
      const ratio = image?.naturalWidth ? image.naturalHeight / image.naturalWidth : 1;
      // Tall artwork keeps its natural proportions without pinning over all options.
      const available = window.innerHeight - (Number.parseFloat(getComputedStyle(this).getPropertyValue('--tahho-preview-top')) || 0);
      const width = preview.clientWidth || preview.parentElement.clientWidth;
      preview.classList.toggle('is-tall-preview', width * ratio > available * .45);
    }
    const changed = preview.hidden === visible;
    preview.hidden = !visible;
    if (changed && before && !matchMedia(desktop).matches && before.bottom > 0 && before.top < window.innerHeight) {
      let desiredTop = before.top;
      if (visible) {
        const header = document.querySelector('header-component')?.getBoundingClientRect().height || 0;
        const bottomBar = this.sticky?.getBoundingClientRect().height || 0;
        desiredTop = Math.min(Math.max(desiredTop, header + preview.offsetHeight + 12), window.innerHeight - bottomBar - before.height - 16);
      }
      window.scrollBy({ top: anchor.getBoundingClientRect().top - desiredTop, behavior: 'instant' });
    }
    if (returnFocus) input?.focus({ preventScroll: true });
  }
  setupCraftMedia(signal) {
    for (const media of this.querySelectorAll('[data-craft-media]')) {
      const video = media.querySelector('video');
      const toggle = media.querySelector('[data-craft-toggle]');
      if (!(video instanceof HTMLVideoElement) || !(toggle instanceof HTMLButtonElement)) continue;
      const update = () => {
        const paused = video.paused;
        media.classList.toggle('is-paused', paused);
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', `${paused ? 'Play' : 'Pause'} craftsmanship video`);
      };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) video.pause();
      toggle.addEventListener('click', event => {
        event.stopPropagation();
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      }, { signal });
      media.addEventListener('click', event => {
        if (event.target === toggle || toggle.contains(event.target)) return;
        media.classList.toggle('is-controls-visible');
      }, { signal });
      video.addEventListener('play', update, { signal });
      video.addEventListener('pause', update, { signal });
      update();
    }
  }
  setupDescriptions(signal) {
    this.resizeObservers ||= [];
    this.querySelectorAll('[data-description]').forEach(description => {
      const preview = description.querySelector('[data-description-preview]');
      const button = description.querySelector('[data-description-open]');
      const dialog = description.querySelector('dialog');
      const update = () => { button.hidden = preview.scrollHeight <= preview.clientHeight + 1; };
      const observer = new ResizeObserver(update); observer.observe(preview); this.resizeObservers.push(observer);
      requestAnimationFrame(update);
      dialog?.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      }, { signal });
      dialog?.addEventListener('close', () => this.descriptionOpener?.focus({ preventScroll: true }), { signal });
    });
  }
  openDescription(button) {
    const dialog = button.closest('[data-description]')?.querySelector('dialog');
    if (!dialog) return;
    this.descriptionOpener = button;
    dialog.showModal();
    dialog.querySelector('[data-description-close]')?.focus();
    emit(this, 'description_expand');
  }
  setupReviews(signal) {
    this.querySelectorAll('[data-review-date]').forEach(time => {
      const parsed = new Date(`${time.dateTime}T12:00:00`);
      const reviewDay = Date.parse(`${time.dateTime}T00:00:00Z`);
      const today = Date.parse(`${time.dataset.today}T00:00:00Z`);
      if (Number.isNaN(parsed.valueOf()) || Number.isNaN(reviewDay) || Number.isNaN(today)) { time.remove(); return; }
      const days = Math.floor((today - reviewDay) / 86400000);
      if (days < 0) { time.dataset.futureDate = 'true'; time.textContent = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(parsed); }
      else if (days < 1) time.textContent = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(0, 'day');
      else if (days < 7) time.textContent = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(-days, 'day');
      else if (days < 56) time.textContent = new Intl.RelativeTimeFormat(undefined, { numeric: 'always' }).format(-Math.floor(days / 7), 'week');
      else time.textContent = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(parsed);
    });
    this.resizeObservers ||= [];
    this.querySelectorAll('[data-review-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-review-track]');
      const update = () => this.updateReviewControls(carousel);
      track?.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true, signal });
      const observer = new ResizeObserver(update); observer.observe(track); this.resizeObservers.push(observer);
      requestAnimationFrame(update);
    });
  }
  stepReviews(carousel, direction) {
    const track = carousel?.querySelector('[data-review-track]');
    const card = track?.firstElementChild;
    if (!track || !card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  updateReviewControls(carousel) {
    const track = carousel.querySelector('[data-review-track]');
    const previous = carousel.querySelector('[data-review-previous]');
    const next = carousel.querySelector('[data-review-next]');
    if (!track || !previous || !next) return;
    previous.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }
  syncBuy() {
    const native = this.nativeButton();
    const sticky = this.querySelector('[data-sticky-buy]');
    if (!sticky || !native) return;
    sticky.disabled = native.disabled || this.pending;
    const text = native.querySelector('.add-to-cart-text')?.textContent?.trim() || native.textContent.trim();
    if (text && sticky.textContent !== text) sticky.textContent = text;
  }
  quantity() {
    return Math.max(1, Number(this.querySelector('.tahho-purchase [name="quantity"]')?.value) || 1);
  }
  syncStickyPrice(quantity = this.quantity()) {
    const price = this.querySelector('[data-sticky-price]');
    const value = price?.querySelector('[data-sticky-price-value]');
    const unit = price?.querySelector('[data-sticky-price-unit]');
    const unitCents = Number(price?.dataset.unitPriceCents);
    const unitFormatted = price?.dataset.unitPriceFormatted;
    if (!price || !value || !unit || !unitCents || !unitFormatted) return;

    const count = Math.max(1, Number(quantity) || 1);
    if (count === 1) {
      value.textContent = unitFormatted;
      unit.textContent = price.dataset.unitLabel || '';
      unit.hidden = !price.dataset.unitLabel;
      return;
    }

    let total;
    try {
      total = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: price.dataset.currency || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format((unitCents * count) / 100);
    } catch {
      total = `${(unitCents * count / 100).toLocaleString()}`;
    }
    value.textContent = `${total} total`;
    unit.textContent = `· ${count} × ${unitFormatted}`;
    unit.hidden = false;
  }
  syncVisibility() {
    if (!this.sticky) return;
    const typing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]), textarea, [contenteditable="true"]');
    const modal = document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])');
    this.sticky.hidden = Boolean(typing || modal || !this.sticky.children.length);
    const header = document.querySelector('header-component');
    const headerHeight = header?.getBoundingClientRect().height || 0;
    this.style.setProperty('--tahho-sticky-offset', `${headerHeight + 24}px`);
    this.style.setProperty('--tahho-preview-top', `${headerHeight + 8}px`);
  }
  validateDimensions() {
    const fields = [...this.querySelectorAll('[data-dimension="width"], [data-dimension="height"]')];
    const quote = this.querySelector('[data-quote-button]');
    if (!quote) return;
    const valid = fields.length === 2 && fields.every(field => field.value && field.checkValidity() && Number(field.value) > 0);
    if (valid && quote.disabled) emit(this, 'wall_dimensions_complete');
    quote.disabled = !valid;
  }
}
if (!customElements.get('tahho-gallery')) customElements.define('tahho-gallery', TahhoGallery);
if (!customElements.get('tahho-product')) customElements.define('tahho-product', TahhoProduct);
